/**
 * components/dashboard/DashboardActivityCard.tsx
 *
 * Dashboard section showing two KPI metrics (to-review, total clients)
 * followed by a list of completed unread form requests. Designed for calm, minimal
 * information density following 2026 SaaS dashboard best practices.
 * Removed "En attente" stat (Apr 2026): no actionable detail visible, source of
 * unnecessary anxiety for the user.
 * Updated (Apr 2026): added keyboard accessibility (tabIndex, role="button",
 * onKeyDown) to clickable request rows so keyboard users can activate them.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../ui/Card';
import ClientAvatar from '../ui/ClientAvatar';
import { formatRelativeDate } from '../../utils/dateUtils';
import { ChevronRight, CheckCircle2 } from 'lucide-react';

export interface ToReviewRequest {
  id: string;
  title: string;
  completed_at: string | null;
  client_id: string | null;
  client_name: string | null;
  client_first_name: string | null;
  client_last_name: string | null;
}

interface DashboardActivityCardProps {
  toReviewCount: number;
  totalClientsCount: number;
  requests: ToReviewRequest[];
  hasMore: boolean;
}

export default function DashboardActivityCard({
  toReviewCount,
  totalClientsCount,
  requests,
  hasMore,
}: DashboardActivityCardProps) {
  const navigate = useNavigate();

  function handleRowKeyDown(e: React.KeyboardEvent<HTMLDivElement>, clientId: string | null) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      clientId ? navigate(`/clients/${clientId}`) : navigate('/clients');
    }
  }

  return (
    <Card className="overflow-hidden">
      <div className="grid grid-cols-2">
        <div className="pr-6 py-2">
          <p className={`text-2xl font-semibold ${toReviewCount > 0 ? 'text-accent-500' : 'text-neutral-900'}`}>
            {toReviewCount}
          </p>
          <p className="text-xs font-medium text-neutral-400 uppercase tracking-wider mt-1.5">
            À consulter
          </p>
        </div>

        <div className="pl-6 py-2 border-l border-neutral-200">
          <p className="text-2xl font-semibold text-neutral-900">{totalClientsCount}</p>
          <p className="text-xs font-medium text-neutral-400 uppercase tracking-wider mt-1.5">
            Clients
          </p>
        </div>
      </div>

      <div className="border-t border-neutral-100 mt-6 -mx-6 -mb-6">
        {requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
            <CheckCircle2 className="w-8 h-8 text-neutral-200 mb-3" />
            <p className="text-sm font-medium text-neutral-500">Rien à traiter pour le moment</p>
            <p className="text-xs text-neutral-400 mt-1">
              Vous serez notifié dès qu'un client complète un formulaire
            </p>
          </div>
        ) : (
          <>
            {requests.map((req, index) => {
              const displayName =
                [req.client_last_name, req.client_first_name].filter(Boolean).join(' ') ||
                req.client_name ||
                null;
              return (
                <div
                  key={req.id}
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    req.client_id
                      ? navigate(`/clients/${req.client_id}`)
                      : navigate('/clients')
                  }
                  onKeyDown={(e) => handleRowKeyDown(e, req.client_id)}
                  className={`flex items-center gap-4 px-6 py-4 hover:bg-neutral-50 cursor-pointer transition-colors group ${index > 0 ? 'border-t border-neutral-100' : ''}`}
                >
                  <ClientAvatar displayName={displayName} hasUnread={true} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 truncate">
                      {displayName || <span className="text-neutral-400">Client sans nom</span>}
                    </p>
                    <p className="text-xs text-neutral-500 truncate mt-0.5">{req.title}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-neutral-400">
                      {formatRelativeDate(req.completed_at)}
                    </span>
                    <ChevronRight className="w-4 h-4 text-neutral-300 group-hover:text-neutral-500 transition-colors" />
                  </div>
                </div>
              );
            })}

            {hasMore && (
              <div className="px-6 py-3 border-t border-neutral-100 bg-neutral-50/50">
                <button
                  onClick={() => navigate('/clients')}
                  className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  Voir tous les clients
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
